import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { checkAnyUserType } from '@utils/Util';

import { toastError, toastSuccess } from '@/components/Toast';
import { closeLoader, openLoader } from '@/store/actions/LoaderAction';
import LoaderComponent from '@/components/LoaderComponent';

import VersionSidebar from './Components/VersionSidebar';
import Header from './Components/Header';
import FabricTable from './Components/FabricTable';
import PrintTable from './Components/PrintTable';
import TrimsSection from './Components/TrimsSection';
import PackagingSection from './Components/PackagingSection';
import CMSection from './Components/CMSection';
import CommercialSection from './Components/CommercialSection';
import CostingSummary from './Components/CostingSummary';
import ActionButtons from './Components/ActionButtons';
import ProductWisePriceRange from './Components/ProductWisePriceRange';
import {
	useGetCostingByProductIdQuery,
	useGetHistoryByProductIdQuery,
	useGetPriceRangeByProductIdQuery,
	useSubmitCostingMutation,
	useGetCostingByHistoryIdQuery,
	useLazyLikeUnlikeCostingQuery,
	useHandleRecostMutation,
	useGetProductInfoByProductIdQuery
} from './api/costingAgentApi';
import { LoadingState } from './Components/LoadingState';
import EmbroideryTable from './Components/EmbroideryTable';
import WashTable from './Components/WashTable';
import { validateRequiredFields as validateRequiredFieldsUtil } from './utils';

const CostingAgent = ({ setLoader }) => {
	const { id } = useParams();
	const [showVersionSidebar, setShowVersionSidebar] = useState(false);
	const [hasChanges, setHasChanges] = useState(false);
	const [formData, setFormData] = useState(null);
	const [selectedHistoryId, setSelectedHistoryId] = useState(null);
	const location = useLocation();
	const navigate = useNavigate();
	const hasProcessedInitialLoad = useRef(false);
	const [shouldFetch, setShouldFetch] = useState(false);
	const [isEmptyHistory, setIsEmptyHistory] = useState(false);
	const [changedSections, setChangedSections] = useState(new Set());
	const [hasSubmittedCosting, setHasSubmittedCosting] = useState(false);
	const [recostResponse, setRecostResponse] = useState(null);
	const [validationErrors, setValidationErrors] = useState({});
	const dispatch = useDispatch();

	const editableRoles = () => {
		return checkAnyUserType(['ADMIN', 'PRODUCT_DEVELOPMENT', 'COSTING_MANAGER']);
	};

	const isEditable = editableRoles();
	const {
		data: costingData,
		refetch: refetchAiCosting,
		isLoading,
		error
	} = useGetCostingByProductIdQuery(id, { skip: !shouldFetch });
	const { data: historyData, refetch: refetchHistory } = useGetHistoryByProductIdQuery(id);
	const [submitCosting] = useSubmitCostingMutation();
	const [handleRecost] = useHandleRecostMutation();
	const [likeUnlikeCosting, { isLoading: isLikeUnlikeLoading }] = useLazyLikeUnlikeCostingQuery();
	const { data: priceRangeData, isLoading: isPriceRangeLoading } = useGetPriceRangeByProductIdQuery(id);
	const { data: productInfoData } = useGetProductInfoByProductIdQuery(id);
	const getHistoryId = () => {
		if (!historyData?.payload || historyData.payload.length === 0) {
			return null;
		}
		const approvedItem = historyData.payload.find((item) => item.approved === true);
		return approvedItem ? approvedItem.id : historyData.payload[0]?.id;
	};
	const historyId = selectedHistoryId || getHistoryId();
	const {
		data: historyCostingData,
		refetch: refetchHistoryCosting,
		isLoading: isHistoryCostingLoading
	} = useGetCostingByHistoryIdQuery({ productId: id, historyId }, { skip: !historyId });

	useEffect(() => {
		if (historyData?.payload && historyData.payload.length === 0) {
			setIsEmptyHistory(true);
		} else {
			setIsEmptyHistory(false);
		}
	}, [historyData]);

	useEffect(() => {
		if (historyCostingData?.payload) {
			setHasSubmittedCosting(historyCostingData?.payload?.isSubmitted || false);
		}
	}, [historyCostingData, selectedHistoryId]);

	useEffect(() => {
		if (historyData?.payload && historyData.payload.length > 0 && !selectedHistoryId) {
			const approvedItem = historyData.payload.find((item) => item.approved === true);
			const initialHistoryId = approvedItem ? approvedItem.id : historyData.payload[0]?.id;
			setSelectedHistoryId(initialHistoryId);
		}
	}, [historyData, selectedHistoryId]);

	useEffect(() => {
		setLoader(false);
	}, [id, setLoader]);

	useEffect(() => {
		if (error) {
			toastError('Error fetching costing data');
		}
	}, [error]);

	useEffect(() => {
		if (!hasProcessedInitialLoad.current && isEmptyHistory) {
			const params = new URLSearchParams(location.search);
			const isAiCostingOnce = params.get('isAiCosting') === 'true';

			if (isAiCostingOnce) {
				setShouldFetch(true);
				hasProcessedInitialLoad.current = true;
				params.delete('isAiCosting');
				navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
			} else {
				hasProcessedInitialLoad.current = true;
			}
		}
	}, [location.search, location.pathname, navigate, isEmptyHistory]);

	useEffect(() => {
		if (costingData?.payload) {
			const payload = costingData?.payload;
			setFormData({ ...payload.costingDetails });
			setSelectedHistoryId(payload?.id);
			setHasChanges(false);
			setChangedSections(new Set());
			setRecostResponse(null);
			refetchHistory();
		}
	}, [costingData, refetchHistory]);

	useEffect(() => {
		if (historyCostingData?.payload) {
			setFormData({ ...historyCostingData.payload.costingDetails });
			setHasChanges(false);
			setChangedSections(new Set());
		}
	}, [historyCostingData]);

	const sectionToChangeType = {
		fabricWisePriceBreakdownList: 'FABRIC_CHANGE',
		printPriceList: 'PRINT_CHANGE',
		embroideryPriceList: 'EMBROIDERY_CHANGE',
		washPriceBreakdown: 'WASH_CHANGE',
		trimsDetailsList: 'TRIMS_CHANGE',
		trimsAdditionalCharge: 'TRIMS_CHANGE',
		packagingPriceCostBreakdown: 'PACKAGING_CHANGE',
		cmCost: 'CM_CHANGE',
		commercialCostPercentage: 'COMMERCIAL_CHANGE'
	};

	const handleDataChange = useCallback((section) => {
		setHasChanges(true);
		if (section && sectionToChangeType[section]) {
			setChangedSections((prev) => new Set([...prev, sectionToChangeType[section]]));
		}
	}, []);

	const validateRequiredFields = () => {
		const errors = validateRequiredFieldsUtil(formData);
		setValidationErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleReCost = async () => {
		if (!historyId || !formData || changedSections.size === 0) return;

		if (!validateRequiredFields()) {
			toastError('Please fill in all required fields');
			return;
		}

		dispatch(openLoader());
		try {
			const changeTypeArray = Array.from(changedSections);
			const response = await handleRecost({
				productId: id,
				historyId,
				requestBody: {
					techpackCostingDataDTO: formData,
					changeTypeList: changeTypeArray
				}
			}).unwrap();

			if (response?.payload) {
				setFormData({ ...response.payload.costingDetails });
				setRecostResponse(response.payload);
				setHasChanges(false);
				setHasSubmittedCosting(false);
				setChangedSections(new Set());
				setSelectedHistoryId(response.payload.id);
				setValidationErrors({});
				refetchHistory();
				toastSuccess('Cost recalculated successfully');
			}
		} catch (error) {
			toastError(error?.data?.message || 'Error recalculating cost');
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleSubmit = async () => {
		if (!historyId) return;

		if (!validateRequiredFields()) {
			toastError('Please fill in all required fields');
			return;
		}

		dispatch(openLoader());
		try {
			await submitCosting({ productId: id, historyId, requestBody: formData }).unwrap();
			setHasSubmittedCosting(true);
			setValidationErrors({});
			refetchHistoryCosting();
			toastSuccess('Costing submitted successfully');
		} catch (error) {
			toastError(error.data.message);
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleLikeUnlike = async (isLiked) => {
		if (!historyId) return;
		try {
			await likeUnlikeCosting({ productId: id, historyId, isLiked });
			refetchHistory();
		} catch (error) {
			toastError(error?.data?.message || 'Failed to update like status');
		}
	};

	const getLikeStatus = () => {
		if (!historyData?.payload || !historyId) return null;
		const selectedItem = historyData.payload.find((item) => item.id === historyId);
		return selectedItem?.isLiked ?? null;
	};

	const isLiked = getLikeStatus();

	const updateFormData = useCallback(
		(section, data) => {
			setFormData((prev) => {
				if (!prev) return prev;
				return {
					...prev,
					[section]: data
				};
			});
			handleDataChange(section);
		},
		[handleDataChange]
	);

	const onGenerateAiCosting = () => {
		if (shouldFetch || costingData) {
			refetchAiCosting();
		}
		setShouldFetch(true);
	};

	const responsePayload =
		recostResponse?.costingDetails ||
		costingData?.payload.costingDetails ||
		historyCostingData?.payload.costingDetails;
	const payload = formData || responsePayload?.costingDetails || {};

	if (isLoading) {
		return (
			<div className="bg-white costing-agent-container d-flex justify-content-center align-items-center">
				<LoadingState isLoading={true} />
			</div>
		);
	}

	if (isEmptyHistory) {
		return (
			<div className="bg-white costing-agent-container d-flex justify-content-center align-items-center">
				<div className="text-center">
					<h3 className="regular-16">No costing has been done yet</h3>
					<p className="regular-14 gray_dark_02 mb-3">
						Get started by using AI to generate an instant <br /> cost breakdown for this design.
					</p>
					<button
						className="button primary size32"
						onClick={onGenerateAiCosting}
						disabled={!isEditable}
					>
						<span>Generate AI Costing</span>
					</button>
				</div>
			</div>
		);
	}

	return (
		<LoaderComponent loading={isHistoryCostingLoading}>
			<div className="costing-agent-container bg-white">
				<Header onHistoryClick={() => setShowVersionSidebar(true)} />
				<div className="costing-table-container relative">
					<div className="costing-table-and-summary d-flex flex-wrap gap-3">
						<div className="all-costing-table mb-4">
							<FabricTable
								data={payload?.fabricWisePriceBreakdownList}
								cadConsumptions={payload?.cadWiseConsumptionList}
								fabricWiseMeasurements={payload?.fabricWiseMeasurements}
								totalCost={payload?.fabricCost || 0}
								onChange={(data) => updateFormData('fabricWisePriceBreakdownList', data)}
								isEditable={isEditable}
								validationErrors={validationErrors}
							/>

							<PrintTable
								data={payload?.printPriceList}
								totalCost={payload?.printCost || 0}
								onChange={(data) => updateFormData('printPriceList', data)}
								isEditable={isEditable}
								validationErrors={validationErrors}
							/>

							<EmbroideryTable
								data={payload?.embroideryPriceList}
								totalCost={payload?.embroideryCost || 0}
								onChange={(data) => updateFormData('embroideryPriceList', data)}
								isEditable={isEditable}
								validationErrors={validationErrors}
							/>

							<WashTable
								data={payload?.washPriceBreakdown}
								totalCost={payload?.washCost || 0}
								onChange={(data) => updateFormData('washPriceBreakdown', data)}
								isEditable={isEditable}
								validationErrors={validationErrors}
							/>
							<TrimsSection
								data={payload?.trimsDetailsList}
								totalCost={payload?.trimsCost || 0}
								onChange={(data) => updateFormData('trimsDetailsList', data)}
								trimsAdditionalCharge={payload?.trimsAdditionalCharge}
								onTrimsAdditionalChargeChange={(value) =>
									updateFormData('trimsAdditionalCharge', value)
								}
								isEditable={isEditable}
							/>
							<PackagingSection
								data={payload?.packagingPriceCostBreakdown}
								totalCost={payload?.packagingCost || 0}
								onChange={(data) => updateFormData('packagingPriceCostBreakdown', data)}
								isEditable={isEditable}
							/>
							<CMSection
								value={payload?.cmCost}
								totalCost={responsePayload?.cmCost || payload?.cmCost || 0}
								onChange={(value) => updateFormData('cmCost', value)}
								isEditable={isEditable}
							/>
							<CommercialSection
								value={payload?.commercialCostPercentage}
								totalCost={payload?.commercialCost || 0}
								onChange={(value) => updateFormData('commercialCostPercentage', value)}
								isEditable={isEditable}
							/>
						</div>
						<div className="costing-summary-container">
							<CostingSummary
								summary={{
									fabricConsumption: payload?.estimatedConsumption || 0,
									fabricCost: payload?.fabricCost || 0,
									printCost: payload?.printCost || 0,
									embroideryCost: payload?.embroideryCost || 0,
									trimsCost: payload?.trimsCost || 0,
									cmCost: responsePayload?.cmCost || payload?.cmCost || 0,
									washCost: payload?.washCost || 0,
									packaging: payload?.packagingCost || 0,
									commercial: payload?.commercialCost || 0
								}}
								isLiked={isLiked}
								onLike={() => handleLikeUnlike(true)}
								onDislike={() => handleLikeUnlike(false)}
								isLoading={isLikeUnlikeLoading}
								totalFobPerUnit={payload?.predictedCost || 0}
							/>
							<ActionButtons
								hasChanges={hasChanges}
								onReCost={handleReCost}
								onSubmit={handleSubmit}
								isEditable={isEditable}
								hasSubmittedCosting={hasSubmittedCosting}
							/>
							<ProductWisePriceRange
								priceRangeData={priceRangeData?.payload || {}}
								isPriceRangeLoading={isPriceRangeLoading}
								predictedCost={payload?.predictedCost || 0}
								category={productInfoData?.subCategoryResponse?.name}
							/>
						</div>
					</div>
				</div>
				{showVersionSidebar && (
					<VersionSidebar
						productId={id}
						selectedHistoryId={selectedHistoryId}
						onHistorySelect={setSelectedHistoryId}
						onClose={() => setShowVersionSidebar(false)}
					/>
				)}
			</div>
		</LoaderComponent>
	);
};

export default CostingAgent;
